import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Checkbox from '@mui/joy/Checkbox';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Alert from '@mui/joy/Alert';
import { Address, CreateAddressDto } from '../../types/address';
import { addressesApi } from '../../api/addresses.api';
import ArgentinaProvinceCityFields from './ArgentinaProvinceCityFields';

interface AddressFormProps {
  clientReference: string;
  address?: Address | null;
  onSaved: (address: Address) => void;
  onCancel: () => void;
}

const AddressForm: React.FC<AddressFormProps> = ({
  clientReference,
  address,
  onSaved,
  onCancel,
}) => {
  const [formData, setFormData] = useState<CreateAddressDto>({
    street: address?.street || '',
    number: address?.number || '',
    city: address?.city || '',
    state: address?.state || '',
    zipCode: address?.zipCode || '',
    referenceDescription: address?.reference_note || '',
    isDefault: address?.isDefault || false,
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    if (!address && !clientReference.trim()) {
      setSaveError('No se pudo asociar la dirección al cliente.');
      return;
    }
    try {
      const savedAddress = address
        ? await addressesApi.update(address.reference, formData)
        : await addressesApi.create(clientReference, formData);
      onSaved(savedAddress);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'No se pudo guardar la dirección',
      );
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ bgcolor: 'background.level1', p: 2, borderRadius: 'md', mb: 2 }}
    >
      {saveError ? (
        <Alert color="danger" size="sm" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      ) : null}
      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <FormControl required>
            <FormLabel>Calle</FormLabel>
            <Input
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} md={6}>
          <FormControl required>
            <FormLabel>Número</FormLabel>
            <Input
              value={formData.number}
              onChange={(e) => setFormData({ ...formData, number: e.target.value })}
            />
          </FormControl>
        </Grid>
        <ArgentinaProvinceCityFields
          stateValue={formData.state}
          cityValue={formData.city}
          onStateCityChange={(state, city) =>
            setFormData({ ...formData, state, city })
          }
        />
        <Grid xs={12} md={6}>
          <FormControl required>
            <FormLabel>Código postal</FormLabel>
            <Input
              value={formData.zipCode}
              onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} md={6}>
          <FormControl>
            <FormLabel>Descripción</FormLabel>
            <Input
              value={formData.referenceDescription || ''}
              onChange={(e) => setFormData({ ...formData, referenceDescription: e.target.value })}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Checkbox
        sx={{ mt: 2 }}
        label="Establecer como dirección principal"
        checked={formData.isDefault}
        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button type="button" variant="outlined" color="neutral" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" color="primary">
          {address ? 'Actualizar' : 'Guardar'}
        </Button>
      </Stack>
    </Box>
  );
};

export default AddressForm;
